import { useEffect, useState } from 'react';
import { Box, Card, RailzBusinessAccount, withStyles, PulloverSelect, convertToNumber } from 'vopay';
import { Title, Button } from 'UI';
import { useForm } from '../../Form/utils';
import { useToast } from '@/providers/Toast.provider';

import useRailzCreateBill from '@/hooks/useApi/useRailzCreateBill';
import useRailzVendors from '@/hooks/useApi/useRailzVendors';
import useRailzTaxRates from '@/hooks/useApi/useRailzTaxRates';
import useRailzTrackingCategories from '@/hooks/useApi/useRailzTrackingCategories';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import useRailzCategories from '@/hooks/useApi/useRailzCategories';
import useFetchRailzBills from '@/hooks/useApi/useFetchRailzBills';
import InvoiceHeader from '../CreateInvoice/InvoiceHeader/InvoiceHeader';
import { useClientData } from '@/providers/ClientData.provider';
import BillTable from './BillTable/BillTable';
import BillBalances from './BillBalances/BillBalances';
import {
  buildInvoiceFormRows,
  buildInvoiceNoteRows,
  buildInvoiceNumberRows,
  InvoiceSubmission,
  LineItem,
} from './buildBillForm.helper';
import styles from './CreateBillPage.module.scss';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addDays, addYears, format } from 'date-fns';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
const classes = withStyles(styles);

const CreateBillPage: React.FC = () => {
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Software' });
  const { vendors, fetchVendors } = useRailzVendors();
  const { taxRates, fetchTaxRates } = useRailzTaxRates();
  const { trackingCategories, fetchTrackingCategories } = useRailzTrackingCategories();
  const { categories, fetchCategories } = useRailzCategories();
  const [railzBusinessAccountResponse, fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [currentRailzAccount, setCurrentRailzAccount] = useState<RailzBusinessAccount | undefined>();
  const [railzBillsResponse, fetchRailzBills] = useFetchRailzBills({});
  const { response } = useClientData();
  const { createBill } = useRailzCreateBill();
  const [isTableValid, setIsTableValid] = useState(false);
  const history = useHistory();
  const toast = useToast();
  const [previousTerms, setPreviousTerms] = useState<
    'Due on Receipt' | 'Custom' | 'Net 15' | 'Net 30' | 'Net 60' | 'Net 80' | 'Net 90' | ''
  >('');
  const [previousPostedDate, setPreviousPostedDate] = useState(new Date().getTime().toString());
  const [previousDueDate, setPreviousDueDate] = useState('');
  const [taxCalculation, setTaxCalculation] = useState<'inclusive' | 'exclusive' | 'outOfScope'>('exclusive');
  const [lines, setLines] = useState<LineItem[]>([]);
  const taxCalculationOptions: {
    label: string;
    value: 'inclusive' | 'exclusive' | 'outOfScope';
  }[] = [
    { label: t('Common.taxCalculation.inclusive', { defaultValue: 'Inclusive' }), value: 'inclusive' },
    { label: t('Common.taxCalculation.exclusive', { defaultValue: 'Exclusive' }), value: 'exclusive' },
    { label: t('Common.taxCalculation.outOfScope', { defaultValue: 'Out of Scope' }), value: 'outOfScope' },
  ];

  useEffect(() => {
    fetchRailzBusinessAccount({});
  }, []);

  useEffect(() => {
    if (railzBusinessAccountResponse) {
      const currentAccount: RailzBusinessAccount | undefined = Object.values(
        railzBusinessAccountResponse.Accounts || {},
      )[0];
      setCurrentRailzAccount(currentAccount);
    }
  }, [railzBusinessAccountResponse]);

  useEffect(() => {
    if (currentRailzAccount) {
      fetchVendors({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });

      fetchTaxRates({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchTrackingCategories({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchCategories({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchRailzBills({
        BusinessName: currentRailzAccount.BusinessName || '',
        ServiceName: currentRailzAccount.ServiceName || '',
        StartDate: format(addYears(new Date(), -20), 'yyyy-MM-dd'),
        EndDate: format(addYears(new Date(), 5), 'yyyy-MM-dd'),
        Limit: 1,
        Status: 'open',
      });
    }
  }, [currentRailzAccount]);
  const billNumber = buildInvoiceNumberRows(t);
  const billNote = buildInvoiceNoteRows(t);
  const billForm = buildInvoiceFormRows(vendors || [], t);

  const { ctx, renderFormElements } = useForm<InvoiceSubmission, InvoiceSubmission>({
    config: [...billNumber, ...billForm, ...billNote],
    initialValues: {
      VendorInvoiceNumber: '',
      PostedDate: new Date().getTime().toString(),
      DueDate: '',
      VendorRefID: '',
      CustomerEmail: '',
      CustomerMailingAddress: '',
      Terms: '',
      Memo: '',
      Lines: [],
    },
    onSubmit: async (serializedData) => {
      if (!currentRailzAccount) return;
      const response = await createBill({
        BusinessName: currentRailzAccount?.BusinessName,
        ServiceName: currentRailzAccount?.ServiceName,
        Bill: {
          ...serializedData,
          Currency: 'CAD',
          CurrencyRate: 1,
          VendorRefID: convertToNumber(serializedData.VendorRefID) || 0,
          Lines: lines.map((line) => {
            const amount = convertToNumber(line.Amount) || 0;
            const taxAmount =
              (amount * (taxRates?.find((rate) => rate.id === line.TaxRateID)?.totalTaxRate || 0)) / 100;
            return {
              ...line,
              BillableStatus: line.BillableStatus ? 'billable' : ('notBillable' as const),
              Amount: convertToNumber(line.Amount) || 0,
              Quantity: convertToNumber(line.Quantity) || 0,
              AccountRefID: convertToNumber(line.AccountRefID) || 0,
              TaxAmount: taxAmount,
              TaxRateID: convertToNumber(line.TaxRateID || '') || 0,
            };
          }),
        },
      });
      if (!response.Success) {
        alertDialog.error('Error', response.ErrorMessage);
      } else {
        history.push('/payables');
        toast.success(t('CreateBill.successMessage', { defaultValue: 'Bill created successfully' }));
      }
    },
  });
  useEffect(() => {
    if (railzBillsResponse?.Success) {
      const latestInvoice = railzBillsResponse.Bills?.[0];
      if (latestInvoice) {
        const invoiceNumber = Number(latestInvoice.vendorInvoiceNumber);
        if (!isNaN(invoiceNumber)) {
          ctx.setFieldValue('VendorInvoiceNumber', String(invoiceNumber + 1));
        } else {
          ctx.setFieldValue('VendorInvoiceNumber', '');
        }
      }
    }
  }, [railzBillsResponse]);

  useEffect(() => {
    if (ctx.values.Terms !== previousTerms) {
      setPreviousTerms(ctx.values.Terms);
      let postedDate = new Date(Number(ctx.values.PostedDate));
      if (ctx.values.PostedDate === '') {
        ctx.setFieldValue('PostedDate', new Date().getTime().toString());
        setPreviousPostedDate(new Date().getTime().toString());
        postedDate = new Date();
      }

      if (ctx.values.Terms === 'Net 15') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 15).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 15).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 30') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 30).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 30).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 60') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 60).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 60).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 80') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 80).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 80).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 90') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 90).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 90).getTime().toString());
      }
      if (ctx.values.Terms === 'Due on Receipt') {
        ctx.setFieldValue('DueDate', postedDate.getTime().toString());
        setPreviousDueDate(postedDate.getTime().toString());
      }
    }
  }, [ctx.values.Terms]);
  useEffect(() => {
    if (ctx.values.PostedDate !== previousPostedDate) {
      setPreviousPostedDate(ctx.values.PostedDate);
      ctx.setFieldValue('Terms', 'Custom');
    }
  }, [ctx.values.PostedDate]);
  useEffect(() => {
    if (ctx.values.DueDate !== previousDueDate) {
      setPreviousDueDate(ctx.values.DueDate);
      ctx.setFieldValue('Terms', 'Custom');
    }
  }, [ctx.values.DueDate]);

  return (
    <div>
      <Box flx="col" gapSize="24px">
        <Title level="1">{t('CreateBill.title', { defaultValue: 'Create Bill' })}</Title>
        <Card style={{ padding: '32px' }}>
          <InvoiceHeader
            name={response.AccountName}
            invoiceField={
              renderFormElements({
                config: billNumber,
                override(fieldData) {
                  return fieldData.template;
                },
              })[0]
            }
          />
          <div className={classes('form')}>
            {renderFormElements({
              config: billForm,
            })}
          </div>
          <div className={classes('dashed')} />

          <div className={classes('tableContainer')}>
            <Box flx="col" gapSize="14px">
              <div className={classes('popoverContainer')}>
                {t('Common.amountsAre', { defaultValue: 'Amounts are' })}
                <PulloverSelect
                  value={taxCalculation}
                  onChange={(value) => {
                    setTaxCalculation(value as 'inclusive' | 'exclusive' | 'outOfScope');
                  }}
                >
                  {taxCalculationOptions.map((option) => (
                    <PulloverSelect.Option key={option.value} value={option.value}>
                      {option.label}
                    </PulloverSelect.Option>
                  ))}
                </PulloverSelect>
              </div>
              {taxRates && trackingCategories && categories && (
                <BillTable
                  onChange={(invoiceItems, isValid) => {
                    ctx.setFieldValue('Lines', invoiceItems);
                    setLines(invoiceItems);
                    setIsTableValid(isValid);
                  }}
                  taxCalculation={taxCalculation}
                  afterHasTouched={!!ctx.touched.Memo}
                  salesTaxes={taxRates}
                  categories={categories}
                  trackingCategories={trackingCategories}
                />
              )}
            </Box>
          </div>
          <div className={classes('dashed')} />
          <div className={classes('totalsContainer')}>
            <BillBalances
              taxRates={taxRates || []}
              salesTax={ctx.values.Lines.map((line) => {
                return {
                  amount: convertToNumber(line.Amount) || 0, //TODO: check why it is not a number??
                  salesTaxId: line.TaxRateID,
                };
              })}
              taxCalculation={taxCalculation}
              textArea={
                renderFormElements({
                  config: billNote,
                })[0]
              }
            />
          </div>
          <div className={classes('dashed')} />
        </Card>

        <Box base="8" gap="sm" jc="fe" ai="center">
          <Button
            onClick={() => {
              history.goBack();
            }}
          >
            {t('Common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={() => {
              ctx.handleSubmit();
            }}
            colorScheme="primary"
            disabled={!isTableValid || !ctx.isValid}
          >
            {t('Common.create', { defaultValue: 'Create' })}
          </Button>
        </Box>
      </Box>

      {/* Add your form or other components here */}
    </div>
  );
};

export default CreateBillPage;
